<mat-card class="provider-status-card">
  <div class="provider-status-card__header">
    <div>
      <h2>Payroll Provider Connections</h2>
      <p>Super Admin summary for configured payroll provider connections.</p>
    </div>
    <button mat-stroked-button type="button" [disabled]="isRefreshingProviderConnectionStatus" (click)="refreshProviderConnectionStatusSummary()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
  </div>

  @if (providerConnectionStatusError) {
    <p class="error-text">{{ providerConnectionStatusError }}</p>
  }

  @if (providerConnectionStatusSummary; as providerSummary) {
    <div class="provider-status-grid">
      <div class="summary-item">
        <span>Configured Providers</span>
        <strong>{{ providerSummary.totals.configuredProviders || 0 }}</strong>
      </div>
      <div class="summary-item">
        <span>Active Connections</span>
        <strong>{{ providerSummary.totals.activeProviderConnections || 0 }}</strong>
      </div>
      <div class="summary-item">
        <span>Companies With Credentials</span>
        <strong>{{ providerSummary.totals.companiesWithActiveProviderCredentials || 0 }}</strong>
      </div>
      <div class="summary-item">
        <span>Credential Records</span>
        <strong>{{ providerSummary.totals.providerCredentialRecords || 0 }}</strong>
      </div>
    </div>
    @if (providerSummary.providers.length === 0) {
      <p class="helper-text">
        No payroll provider connections are currently configured.
      </p>
    }
    @if (providerSummary.providers.length > 0) {
      <div class="provider-status-list">
        @for (provider of providerSummary.providers; track trackByProviderConnectionStatus($index, provider)) {
          <div
            class="provider-status-row"
            >
            <div class="provider-status-row__title">
              <div>
                <strong>{{ provider.name }}</strong>
                <span>{{ provider.providerSlug }}</span>
              </div>
              <span class="provider-status-pill" [ngClass]="getProviderStatusPillClass(provider.status)">
                {{ getProviderStatusLabel(provider.status) }}
              </span>
            </div>
            <div class="provider-status-row__metrics">
              <span>{{ provider.activeConnections || 0 }}/{{ provider.totalConnections || 0 }} active</span>
              <span>{{ provider.activeCredentialCompanies || 0 }} with credentials</span>
              <span>{{ provider.availableSettings || 0 }} available settings</span>
              <span>Statuses: {{ getConnectionStatusSummary(provider) }}</span>
              <span>Last verified: {{ provider.latestVerifiedAt ? (provider.latestVerifiedAt | date:'short') : '---' }}</span>
            </div>
          </div>
        }
      </div>
    }
  }
</mat-card>

<mat-card class="ukg-status-card">
  <div class="ukg-status-card__header">
    <div>
      <h2>UKG Sandbox Status</h2>
      <p>Super Admin summary for the shared UKG Ready sandbox environment.</p>
    </div>
    <div class="ukg-status-card__actions">
      <button mat-stroked-button type="button" [disabled]="isRefreshingUkgStatus" (click)="refreshUkgStatusSummary()">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
      <button mat-raised-button color="primary" type="button" [disabled]="isRefreshingUkgStatus" (click)="recheckUkgStatus('impacted')">
        Re-check Impacted
      </button>
      <button mat-stroked-button type="button" [disabled]="isRefreshingUkgStatus" (click)="recheckUkgStatus('all')">
        Re-check All
      </button>
    </div>
  </div>

  @if (ukgStatusError) {
    <p class="error-text">{{ ukgStatusError }}</p>
  }
  @if (ukgStatusRefreshMessage) {
    <p class="helper-text">
      {{ ukgStatusRefreshMessage }}@if (ukgStatusRefreshAt) {
      <span> Updated {{ ukgStatusRefreshAt | date:'shortTime' }}.</span>
    }
  </p>
}

@if (ukgStatusSummary; as summary) {
  <div class="ukg-status-card__summary">
    <span class="ukg-status-pill" [ngClass]="getUkgStatusPillClass(summary.overallState)">
      {{ getUkgStatusLabel(summary.overallState) }}
    </span>
    <span class="ukg-status-card__env">
      {{ summary.environmentLabel | titlecase }}@if (summary.host) {
      <span> • {{ summary.host }}</span>
    }
  </span>
</div>
<div class="ukg-status-grid">
  <div class="summary-item">
    <span>Impacted Companies</span>
    <strong>{{ summary.impactedCompanyCount || 0 }}/{{ summary.totalConfiguredCompanies || 0 }}</strong>
  </div>
  <div class="summary-item">
    <span>Outage Started</span>
    <strong>{{ summary.currentIncidentStartedAt ? (summary.currentIncidentStartedAt | date:'short') : '---' }}</strong>
  </div>
  <div class="summary-item">
    <span>Outage Age</span>
    <strong>{{ getUkgOutageAge(summary) }}</strong>
  </div>
  <div class="summary-item">
    <span>Last Success</span>
    <strong>{{ summary.lastSuccessAt ? (summary.lastSuccessAt | date:'short') : '---' }}</strong>
  </div>
  <div class="summary-item">
    <span>Last Failed Check</span>
    <strong>{{ summary.lastFailureAt ? (summary.lastFailureAt | date:'short') : '---' }}</strong>
  </div>
  <div class="summary-item">
    <span>Vendor Escalation</span>
    <strong>{{ summary.shouldContactVendor ? 'Contact UKG' : 'Monitor' }}</strong>
  </div>
</div>
@if (summary.shouldContactVendor) {
  <div class="ukg-status-escalation">
    UKG sandbox downtime has exceeded {{ summary.escalationThresholdHours }} hours. Reach out to UKG if the outage is still blocking validation.
  </div>
}
}
</mat-card>

@if (isUkgCleanupVisible) {
  <mat-card class="ukg-cleanup-card">
    <div class="ukg-cleanup-header">
      <h2>UKG Data Cleanup</h2>
      <p>
        Run scoped cleanup for UKG-imported data with a dry-run preview before executing destructive changes.
      </p>
      <p class="cleanup-best-practice">
        Recommended full reset: run <strong>Companies</strong> scope, preview with dry run first, then execute.
        If needed, enable <strong>Delete orphan user accounts</strong> in that same run.
      </p>
    </div>
    @if (!isUkgCleanupDisabledInProduction) {
      <div class="ukg-cleanup-grid">
        <div class="cleanup-block">
          <h3>Cleanup Scope</h3>
          <mat-radio-group class="cleanup-level-group" [(ngModel)]="cleanupOptions.level">
            @for (option of cleanupLevelOptions; track option) {
              <mat-radio-button
                class="cleanup-level-option"
                [value]="option.value">
                <div class="cleanup-level-copy">
                  <span class="cleanup-level-title">{{ option.label }}</span>
                  <span class="cleanup-level-description">{{ option.description }}</span>
                </div>
              </mat-radio-button>
            }
          </mat-radio-group>
        </div>
        <div class="cleanup-block">
          <h3>Target Companies</h3>
          <mat-checkbox [(ngModel)]="limitToSelectedCompanies">
            Limit cleanup to selected companies
          </mat-checkbox>
          @if (limitToSelectedCompanies) {
            <mat-form-field class="cleanup-field" appearance="outline">
              <mat-label>UKG Companies</mat-label>
              <mat-select multiple [(ngModel)]="selectedCleanupCompanyIds">
                @for (company of ukgCompanies; track company) {
                  <mat-option [value]="company.id">
                    {{ company.name }} ({{ company.id }})
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          @if (limitToSelectedCompanies && ukgCompanies.length === 0) {
            <p class="helper-text">
              No UKG companies are currently available from the API.
            </p>
          }
          <mat-form-field class="cleanup-field" appearance="outline">
            <mat-label>Additional Company IDs (optional)</mat-label>
            <textarea
              matInput
              rows="3"
              placeholder="Paste company IDs separated by commas or new lines"
            [(ngModel)]="manualCleanupCompanyIds"></textarea>
          </mat-form-field>
        </div>
        <div class="cleanup-block">
          <h3>Safety Options</h3>
          <mat-checkbox [(ngModel)]="cleanupOptions.dryRun">
            Dry run only (no records deleted)
          </mat-checkbox>
          <mat-checkbox [(ngModel)]="cleanupOptions.deleteUserAccounts">
            Delete orphan user accounts after cleanup
          </mat-checkbox>
          <mat-checkbox
            [(ngModel)]="cleanupOptions.forceDeleteUsers"
            [disabled]="!cleanupOptions.deleteUserAccounts">
            Force delete users (attempt cleanup of remaining memberships/roles)
          </mat-checkbox>
          <mat-checkbox
            [(ngModel)]="cleanupOptions.onlyPendingInviteUsers"
            [disabled]="!cleanupOptions.deleteUserAccounts">
            Only delete pending-invite users
          </mat-checkbox>
          <mat-checkbox [(ngModel)]="cleanupOptions.includeNonUkgCompanies">
            Include non-UKG companies when explicit company IDs are provided
          </mat-checkbox>
          <mat-form-field class="cleanup-field" appearance="outline">
            <mat-label>Excluded User Emails (optional)</mat-label>
            <textarea
              matInput
              rows="3"
              [disabled]="!cleanupOptions.deleteUserAccounts"
              placeholder="Paste emails separated by commas or new lines"
            [(ngModel)]="excludedUserEmailsInput"></textarea>
          </mat-form-field>
          @if (cleanupOptions.deleteUserAccounts) {
            <p class="helper-text warning">
              User deletion only happens when backend orphan and payroll-import checks pass.
            </p>
          }
          @if (!cleanupOptions.deleteUserAccounts) {
            <p class="helper-text">
              Excluded user emails are only applied when "Delete orphan user accounts" is enabled.
            </p>
          }
          @if (cleanupOptions.deleteUserAccounts && cleanupOptions.forceDeleteUsers) {
            <p class="helper-text warning">
              Force delete will try to remove remaining team memberships and company roles before deleting each user.
              Review a dry run first.
            </p>
          }
        </div>
      </div>
      <div class="cleanup-actions">
        <button
          mat-raised-button
          color="primary"
          [disabled]="isRunningCleanup"
          (click)="runCleanup()">
          {{ getCleanupPrimaryActionLabel() }}
        </button>
        @if (!cleanupOptions.dryRun) {
          <button
            mat-stroked-button
            [disabled]="isRunningCleanup"
            (click)="runCleanup(true)">
            Run Dry Run Preview
          </button>
        }
        <button
          mat-button
          [disabled]="isRunningCleanup"
          (click)="resetCleanupForm()">
          Reset Options
        </button>
      </div>
      @if (cleanupResult || cleanupError) {
        <div class="cleanup-result">
          <div class="cleanup-result-header">
            <h3>Last Cleanup Result</h3>
            @if (cleanupRunAt) {
              <span class="helper-text">{{ cleanupRunAt | date:'short' }}</span>
            }
          </div>
          @if (cleanupError) {
            <p class="error-text">{{ cleanupError }}</p>
          }
          @if (cleanupResult; as result) {
            <div class="cleanup-summary-grid">
              <div class="summary-item">
                <span>Companies Processed</span>
                <strong>{{ result.companiesProcessed || 0 }}</strong>
              </div>
              <div class="summary-item">
                <span>Teams Deleted</span>
                <strong>{{ result.totals?.teamsDeleted || 0 }}</strong>
              </div>
              <div class="summary-item">
                <span>Memberships Removed</span>
                <strong>{{ result.totals?.teamMembershipsRemoved || 0 }}</strong>
              </div>
              <div class="summary-item">
                <span>Companies Deleted</span>
                <strong>{{ result.totals?.companiesDeleted || 0 }}</strong>
              </div>
              <div class="summary-item">
                <span>Users Deleted</span>
                <strong>{{ result.totals?.usersDeleted || 0 }}</strong>
              </div>
              <div class="summary-item">
                <span>User Deletes Skipped</span>
                <strong>{{ result.totals?.usersSkipped || 0 }}</strong>
              </div>
              <div class="summary-item">
                <span>Forced User Deletes</span>
                <strong>{{ result.totals?.usersForceDeleted || 0 }}</strong>
              </div>
            </div>
            @if (result.missingCompanyIds?.length > 0) {
              <p class="helper-text">
                Missing company IDs: {{ result.missingCompanyIds.join(', ') }}
              </p>
            }
            @if (result.companies?.length > 0) {
              <div class="cleanup-visibility-toggle">
                <mat-checkbox [(ngModel)]="hideNoActionCompanies">
                  Hide companies with no action
                </mat-checkbox>
              </div>
            }
            @if (result.companies?.length > 0) {
              <mat-expansion-panel class="cleanup-details-panel">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    Company Details ({{ getVisibleCompanyDetails(result).length }})
                  </mat-panel-title>
                </mat-expansion-panel-header>
                @if (getVisibleCompanyDetails(result).length === 0) {
                  <p class="helper-text">
                    No companies with actionable cleanup results.
                  </p>
                }
                @for (company of getVisibleCompanyDetails(result); track trackByCleanupCompany($index, company)) {
                  <div class="cleanup-company-row">
                    <div class="cleanup-company-header">
                      <strong>{{ company.companyName || company.companyId }}</strong>
                      <span class="helper-text">{{ company.companyId }}</span>
                    </div>
                    <div class="cleanup-company-metrics">
                      <span>Teams matched: {{ company.teamsMatched?.length || 0 }}</span>
                      <span>Teams deleted: {{ company.teamsDeleted || 0 }}</span>
                      <span>Memberships removed: {{ company.teamMembershipsRemoved || 0 }}</span>
                      <span>Company deleted: {{ company.companyDeleted ? 'Yes' : 'No' }}</span>
                    </div>
                    @if ((company.teamCleanupDetails?.length || 0) > 0) {
                      <div class="cleanup-team-preview">
                        <strong>Teams Targeted</strong>
                        <div class="cleanup-team-preview-list">
                          @for (teamDetail of company.teamCleanupDetails; track trackByCleanupTeamDetail($index, teamDetail)) {
                            <div class="cleanup-team-preview-row">
                              <div class="cleanup-team-preview-header">
                                <span>{{ teamDetail.teamName || 'Unnamed Team' }} ({{ teamDetail.teamId }})</span>
                                    <span class="cleanup-team-status" [ngClass]="{
                                        'cleanup-team-status--would-delete': teamDetail.action === 'would_delete',
                                        'cleanup-team-status--deleted': teamDetail.action === 'deleted',
                                        'cleanup-team-status--failed': teamDetail.action === 'failed',
                                        'cleanup-team-status--missing': teamDetail.action === 'missing_already',
                                        'cleanup-team-status--memberships': teamDetail.action === 'memberships_only'
                                    }">
                                  {{ getCleanupTeamActionLabel(teamDetail) }}
                                </span>
                              </div>
                              @if ((teamDetail.paymentMethodsFound || 0) > 0) {
                                <p class="helper-text">
                                  Payment methods tied to team: {{ teamDetail.paymentMethodsFound }}
                                </p>
                              }
                              @if (teamDetail.errors?.length > 0) {
                                <p class="error-text">
                                  Team cleanup errors: {{ teamDetail.errors.join(' | ') }}
                                </p>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    }
                    @if (company.errors?.length > 0) {
                      <p class="error-text">
                        {{ company.errors.length }} error(s): {{ company.errors.join(' | ') }}
                      </p>
                    }
                  </div>
                }
              </mat-expansion-panel>
            }
            @if (hasUserCleanupDetails(result)) {
              <mat-expansion-panel class="cleanup-details-panel">
                <mat-expansion-panel-header>
                  <mat-panel-title>User Cleanup Details</mat-panel-title>
                </mat-expansion-panel-header>
                @for (company of getVisibleUserCleanupCompanies(result); track trackByCleanupCompany($index, company)) {
                  <div class="cleanup-user-company">
                    <h4>{{ company.companyName || company.companyId }} ({{ company.companyId }})</h4>
                    @if ((company.userCleanup?.details || []).length === 0) {
                      <p class="helper-text">
                        No user cleanup candidates found for this company.
                      </p>
                    }
                    @for (detail of company.userCleanup?.details || []; track trackByCleanupUser($index, detail)) {
                      <div class="cleanup-user-row">
                        <div class="cleanup-user-topline">
                          <div>
                            <strong>{{ detail.userId }}</strong>
                            @if (detail.userEmail) {
                              <span class="helper-text"> · {{ detail.userEmail }}</span>
                            }
                          </div>
                            <span class="cleanup-user-status" [ngClass]="{
                                'cleanup-user-status--would-delete': detail.action === 'would_delete',
                                'cleanup-user-status--deleted': detail.action === 'deleted',
                                'cleanup-user-status--failed': detail.action === 'failed',
                                'cleanup-user-status--skipped': detail.action === 'skipped'
                            }">
                            {{ getCleanupUserActionLabel(detail) }}
                            @if (detail.forced) {
                              (forced)
                            }
                          </span>
                        </div>
                        <p class="helper-text">Reason: {{ detail.reason }}</p>
                        @if (detail.blockers?.length > 0) {
                          <p class="helper-text">
                            Blockers: {{ detail.blockers.join(', ') }}
                          </p>
                        }
                        @if (detail.preCleanupBlockers?.length > 0) {
                          <p class="helper-text">
                            Pre-cleanup blockers: {{ detail.preCleanupBlockers.join(', ') }}
                          </p>
                        }
                        @if (detail.resolvedByCurrentCleanup?.length > 0) {
                          <p class="helper-text">
                            Resolved by current cleanup: {{ detail.resolvedByCurrentCleanup.join(', ') }}
                          </p>
                        }
                        <div class="cleanup-user-metrics">
                          <span>Team memberships removed: {{ detail.removedTeamMemberships || 0 }}</span>
                          <span>Company roles removed: {{ detail.removedCompanyRoles || 0 }}</span>
                          <span>
                            Vendor products removed: {{ detail.userVendorProductsDeleted || 0 }}
                            @if ((detail.userVendorProductsFound || 0) > 0) {
                              (found: {{ detail.userVendorProductsFound }})
                            }
                          </span>
                        </div>
                        @if ((getInitialTeamMemberships(detail).length || 0) > 0 || (getInitialCompanyRoles(detail).length || 0) > 0 || (detail.vendorProductsTargeted?.length || 0) > 0) {
                          <div class="cleanup-user-lists">
                            @if ((getInitialTeamMemberships(detail).length || 0) > 0) {
                              <div class="cleanup-user-list-block">
                                <strong>Team Memberships Found</strong>
                                <ul>
                                  @for (membership of getInitialTeamMemberships(detail); track membership) {
                                    <li>
                                      {{ formatCleanupTeamMembership(membership) }}
                                    </li>
                                  }
                                </ul>
                              </div>
                            }
                            @if ((getInitialCompanyRoles(detail).length || 0) > 0) {
                              <div class="cleanup-user-list-block">
                                <strong>Company Roles Found</strong>
                                <ul>
                                  @for (companyRole of getInitialCompanyRoles(detail); track companyRole) {
                                    <li>
                                      {{ formatCleanupCompanyRole(companyRole) }}
                                    </li>
                                  }
                                </ul>
                              </div>
                            }
                            @if ((detail.vendorProductsTargeted?.length || 0) > 0) {
                              <div class="cleanup-user-list-block">
                                <strong>Vendor Products Targeted</strong>
                                <ul>
                                  @for (vendorProduct of detail.vendorProductsTargeted; track vendorProduct) {
                                    <li>
                                      {{ formatCleanupVendorProduct(vendorProduct) }}
                                    </li>
                                  }
                                </ul>
                              </div>
                            }
                          </div>
                        }
                        @if (detail.forceDeleteErrors?.length > 0) {
                          <p class="error-text">
                            Force-delete cleanup errors: {{ detail.forceDeleteErrors.join(' | ') }}
                          </p>
                        }
                        @if (detail.userVendorProductCleanupErrors?.length > 0) {
                          <p class="error-text">
                            Vendor product cleanup errors: {{ detail.userVendorProductCleanupErrors.join(' | ') }}
                          </p>
                        }
                      </div>
                    }
                  </div>
                }
              </mat-expansion-panel>
            }
            <mat-expansion-panel class="cleanup-details-panel">
              <mat-expansion-panel-header>
                <mat-panel-title>Raw Response</mat-panel-title>
              </mat-expansion-panel-header>
              <pre class="cleanup-json">{{ result | json }}</pre>
            </mat-expansion-panel>
          }
        </div>
      }
    } @else {
      <p class="helper-text warning ukg-cleanup-disabled-message">
        UKG data cleanup is disabled in production.
      </p>
    }
  </mat-card>
}
